import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { payrollAPI, staffLeaveAPI } from '../../services/api';
import { gapLabel, roleLabel, statuteLabel } from './plainLabels';
import { FieldGrid, PageHeader, Section } from './payrollUi';

const empty = {
  firstName: '', lastName: '', middleName: '', displayName: '', gender: '', maritalStatus: '',
  dateOfBirth: '', nationality: '', phone: '', address: '', city: '', state: '', country: '',
  permanentAddress: '', personalEmail: '', totalExperience: '', relevantExperience: '',
  emergencyName: '', emergencyPhone: '', emergencyRelation: '', aadhaarNumber: '',
  dateOfJoining: '', exitDate: '', payeeType: '', designation: '', employeeCode: '', officialEmail: '', reportingManagerUserId: '',
  pan: '', bankAccountName: '', bankName: '', bankAccountNumber: '', bankIfsc: '', bankBranch: '',
  upiId: '', googlePay: '', pfChoice: '', uan: '', tdsSection: '', previousPfEsiNote: '',
};

const DAYS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const dateField = { type: 'date', InputLabelProps: { shrink: true }, fullWidth: true };

export default function StaffProfilePage() {
  const qc = useQueryClient();
  const people = useQuery(['pay-people'], async () => (await payrollAPI.listPeople()).data.data);
  const [params] = useSearchParams();
  const [userId, setUserId] = useState(() => params.get('person') || '');
  useEffect(() => {
    const person = params.get('person');
    if (person) setUserId(person);
  }, [params]);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);
  const [form, setForm] = useState(empty);
  const [location, setLocation] = useState({
    placeOfPostingId: '', workArrangement: 'office', approvedAddress: '', approvedCity: '', approvedState: '',
    effectiveFrom: '', changeReason: '',
  });
  const profile = useQuery(['staff-profile', userId], async () => (await payrollAPI.getProfile(userId)).data.data, { enabled: Boolean(userId) });
  const templates = useQuery(['staff-leave-templates'], async () => (await staffLeaveAPI.listTemplates()).data.data);
  const calendars = useQuery(['staff-leave-calendars'], async () => (await staffLeaveAPI.listCalendars()).data.data);
  const setup = useQuery(['pay-setup'], async () => (await payrollAPI.getSetup()).data.data);

  useEffect(() => {
    const p = profile.data?.profile;
    const user = profile.data?.user;
    if (!p && !user) return;
    setForm({
      firstName: user?.first_name || '',
      lastName: user?.last_name || '',
      middleName: p?.middle_name || '',
      displayName: p?.display_name || '',
      gender: p?.gender || '',
      maritalStatus: p?.marital_status || '',
      dateOfBirth: user?.date_of_birth ? String(user.date_of_birth).slice(0, 10) : '',
      nationality: p?.nationality || '',
      phone: user?.phone || '',
      address: user?.address || '',
      city: user?.city || '',
      state: user?.state || '',
      country: user?.country || '',
      permanentAddress: p?.permanent_address || '',
      personalEmail: p?.personal_email || '',
      totalExperience: p?.total_experience ?? '',
      relevantExperience: p?.relevant_experience ?? '',
      emergencyName: p?.emergency_name || '',
      emergencyPhone: p?.emergency_phone || '',
      emergencyRelation: p?.emergency_relation || '',
      aadhaarNumber: p?.aadhaar_number || '',
      dateOfJoining: p?.date_of_joining ? String(p.date_of_joining).slice(0, 10) : '',
      exitDate: p?.exit_date ? String(p.exit_date).slice(0, 10) : '',
      payeeType: p?.payee_type || '',
      designation: profile.data?.designation?.designation || '',
      employeeCode: p?.employee_code || '',
      officialEmail: p?.official_email || '',
      reportingManagerUserId: p?.reporting_manager_user_id || '',
      pan: p?.pan || '',
      bankAccountName: p?.bank_account_name || '',
      bankName: p?.bank_name || '',
      bankAccountNumber: p?.bank_account_number || '',
      bankIfsc: p?.bank_ifsc || '',
      bankBranch: p?.bank_branch || '',
      upiId: p?.upi_id || '',
      googlePay: p?.google_pay || '',
      pfChoice: p?.pf_choice || '',
      uan: p?.uan || '',
      tdsSection: p?.tds_section || '',
      previousPfEsiNote: p?.previous_pf_esi_note || '',
    });
  }, [profile.data]);

  const onOk = (message) => ({
    onSuccess: () => {
      qc.invalidateQueries(['staff-profile']);
      qc.invalidateQueries(['pay-people']);
      toast.success(message);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Request failed'),
  });
  const saveProfile = useMutation((body) => payrollAPI.saveProfile(userId, body), onOk('Profile saved'));
  const saveLocation = useMutation((body) => payrollAPI.saveLocation(userId, body), onOk('Location saved'));
  const acknowledge = useMutation((body) => payrollAPI.acknowledgeLocation(userId, body), onOk('Declaration recorded'));
  const saveSalary = useMutation((body) => payrollAPI.saveSalary(userId, body), onOk('Salary saved'));

  const list = useMemo(() => people.data || [], [people.data]);
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => String(p.name || '').toLowerCase().includes(q) || String(p.role || '').includes(q));
  }, [list, search]);
  const selected = list.find((p) => p.id === userId);
  const data = profile.data;
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const readyCount = list.filter((p) => p.complete).length;

  const saveBody = {
    ...form,
    dateOfBirth: form.dateOfBirth || null,
    exitDate: form.exitDate || null,
    totalExperience: form.totalExperience === '' ? null : Number(form.totalExperience),
    relevantExperience: form.relevantExperience === '' ? null : Number(form.relevantExperience),
    uan: form.uan || null,
    tdsSection: form.tdsSection || null,
    pfChoice: form.pfChoice || null,
  };

  return (
    <Stack spacing={2}>
      <PageHeader
        title="Staff profiles"
        subtitle="Pick a person on the left. The list shows what is still missing. Save each section after you fill it in."
      />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '300px 1fr' }, gap: 2, alignItems: 'start' }}>
        <Section title="People" hint={`${readyCount} of ${list.length} ready for payroll`}>
          <TextField size="small" fullWidth placeholder="Search name or role" value={search} onChange={(e) => setSearch(e.target.value)} />
          <List dense sx={{ maxHeight: 640, overflow: 'auto', mx: -1 }}>
            {visible.map((p) => (
              <ListItemButton key={p.id} selected={p.id === userId} onClick={() => setUserId(p.id)}>
                <ListItemText primary={p.name} secondary={roleLabel(p.role)} />
                <Chip size="small" label={p.complete ? 'Ready' : `${p.missing.length} left`} color={p.complete ? 'success' : 'warning'} />
              </ListItemButton>
            ))}
            {visible.length === 0 && <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>No one matches that search.</Typography>}
          </List>
        </Section>

        {!userId && (
          <Section title="No person selected" hint="Choose someone from the list to edit their profile, pay, and work location." />
        )}

        {userId && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="h6">{selected?.name}</Typography>
              {selected?.complete
                ? <Chip size="small" color="success" label="Ready for payroll" />
                : (selected?.missing || []).map((item) => <Chip key={item} size="small" color="warning" label={gapLabel(item)} />)}
            </Stack>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
              <Tab label="Person" />
              <Tab label="Pay" />
              <Tab label="Work" />
              <Tab label="History" />
            </Tabs>

            {tab === 0 && (
              <Stack spacing={2}>
                <Section title="Name and contact" action={<Button variant="contained" onClick={() => saveProfile.mutate(saveBody)} disabled={saveProfile.isLoading}>Save person</Button>}>
                  <FieldGrid>
                    <TextField label="First name" value={form.firstName} onChange={set('firstName')} fullWidth />
                    <TextField label="Middle name" value={form.middleName} onChange={set('middleName')} fullWidth />
                    <TextField label="Last name" value={form.lastName} onChange={set('lastName')} fullWidth />
                    <TextField label="Name on the payslip" value={form.displayName} onChange={set('displayName')} fullWidth />
                    <TextField select label="Gender" value={form.gender} onChange={set('gender')} fullWidth>
                      <MenuItem value="">Not set</MenuItem>
                      <MenuItem value="Male">Male</MenuItem>
                      <MenuItem value="Female">Female</MenuItem>
                    </TextField>
                    <TextField label="Date of birth" value={form.dateOfBirth} onChange={set('dateOfBirth')} {...dateField} />
                    <TextField label="Phone" value={form.phone} onChange={set('phone')} fullWidth />
                    <TextField label="Personal email" value={form.personalEmail} onChange={set('personalEmail')} fullWidth />
                    <TextField label="Current address" value={form.address} onChange={set('address')} fullWidth />
                    <TextField label="City" value={form.city} onChange={set('city')} fullWidth />
                    <TextField label="State" value={form.state} onChange={set('state')} fullWidth />
                    <TextField label="Country" value={form.country} onChange={set('country')} fullWidth />
                  </FieldGrid>
                </Section>
                <Section title="Job" hint="Payroll needs employee or contractor, joining date, PAN, and bank details.">
                  <FieldGrid>
                    <TextField select label="Employee or contractor" value={form.payeeType} onChange={set('payeeType')} fullWidth>
                      <MenuItem value="">Not set</MenuItem>
                      <MenuItem value="employee">Employee</MenuItem>
                      <MenuItem value="contractor">Contractor</MenuItem>
                    </TextField>
                    <TextField label="Job title" value={form.designation} onChange={set('designation')} fullWidth />
                    <TextField label="Employee code" value={form.employeeCode} onChange={set('employeeCode')} fullWidth />
                    <TextField label="Official email" value={form.officialEmail} onChange={set('officialEmail')} helperText="Blank keeps the login email" fullWidth />
                    <TextField label="Date of joining" value={form.dateOfJoining} onChange={set('dateOfJoining')} {...dateField} />
                    <TextField label="Exit date" value={form.exitDate} onChange={set('exitDate')} helperText="Leave blank until they leave" {...dateField} />
                    <TextField label="PAN" value={form.pan} onChange={set('pan')} fullWidth />
                    <TextField select label="Reports to" value={String(form.reportingManagerUserId || '')} onChange={set('reportingManagerUserId')} helperText="For sales, choose their sales supervisor. Teachers keep the supervisor already assigned." fullWidth>
                      <MenuItem value="">No one</MenuItem>
                      {form.reportingManagerUserId && !list.some((person) => String(person.id) === String(form.reportingManagerUserId)) && (
                        <MenuItem value={String(form.reportingManagerUserId)}>Already chosen</MenuItem>
                      )}
                      {list.filter((person) => person.id !== userId).map((person) => (
                        <MenuItem key={person.id} value={String(person.id)}>{person.name}</MenuItem>
                      ))}
                    </TextField>
                  </FieldGrid>
                </Section>
                <Section title="Bank and provident fund">
                  <FieldGrid>
                    <TextField label="Account name" value={form.bankAccountName} onChange={set('bankAccountName')} fullWidth />
                    <TextField label="Bank" value={form.bankName} onChange={set('bankName')} fullWidth />
                    <TextField label="Account number" value={form.bankAccountNumber} onChange={set('bankAccountNumber')} fullWidth />
                    <TextField label="IFSC" value={form.bankIfsc} onChange={set('bankIfsc')} fullWidth />
                    <TextField label="Branch" value={form.bankBranch} onChange={set('bankBranch')} fullWidth />
                    <TextField label="UPI" value={form.upiId} onChange={set('upiId')} fullWidth />
                    <TextField select label="Provident fund" value={form.pfChoice} onChange={set('pfChoice')} fullWidth>
                      <MenuItem value="">Not set</MenuItem>
                      <MenuItem value="applicable">Yes, deduct it</MenuItem>
                      <MenuItem value="not_applicable">No</MenuItem>
                    </TextField>
                    <TextField label="UAN" value={form.uan} onChange={set('uan')} helperText="Needed when provident fund is yes" fullWidth />
                    <TextField label="Tax section" value={form.tdsSection} onChange={set('tdsSection')} helperText="Contractors only. Example: professional fees" fullWidth />
                  </FieldGrid>
                </Section>
              </Stack>
            )}

            {tab === 1 && (
              <Stack spacing={2}>
                <SalaryLines payeeType={form.payeeType} templates={setup.data?.templates || []} onSave={(body) => saveSalary.mutate(body)} />
                <IncentiveAndStatute userId={userId} categories={(setup.data?.categories || []).filter((c) => c.is_active)} statutes={setup.data?.statutes || []} />
              </Stack>
            )}

            {tab === 2 && (
              <Stack spacing={2}>
                <WorkPattern
                  userId={userId}
                  role={data?.user?.role}
                  country={data?.user?.country}
                  form={form}
                  pattern={data?.pattern}
                  leaveTemplateId={data?.leaveAssignment?.template_id || ''}
                  holidayCalendarId={data?.holidayAssignment?.calendar_id || ''}
                  templates={templates.data || []}
                  calendars={calendars.data || []}
                />
                <Section
                  title="Work location"
                  hint="Office, hybrid, or work from home. A change needs a reason."
                  action={<Button variant="contained" onClick={() => saveLocation.mutate(location)} disabled={saveLocation.isLoading}>Save location</Button>}
                >
                  <FieldGrid>
                    <TextField select label="Place of posting" value={location.placeOfPostingId} onChange={(e) => setLocation({ ...location, placeOfPostingId: e.target.value })} fullWidth>
                      {(data?.postings || []).map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                    </TextField>
                    <TextField select label="Arrangement" value={location.workArrangement} onChange={(e) => setLocation({ ...location, workArrangement: e.target.value })} fullWidth>
                      <MenuItem value="office">Office</MenuItem>
                      <MenuItem value="hybrid">Hybrid</MenuItem>
                      <MenuItem value="work_from_home">Work from home</MenuItem>
                    </TextField>
                    <TextField label="Approved address" value={location.approvedAddress} onChange={(e) => setLocation({ ...location, approvedAddress: e.target.value })} fullWidth />
                    <TextField label="City" value={location.approvedCity} onChange={(e) => setLocation({ ...location, approvedCity: e.target.value })} fullWidth />
                    <TextField label="State" value={location.approvedState} onChange={(e) => setLocation({ ...location, approvedState: e.target.value })} fullWidth />
                    <TextField label="Effective from" value={location.effectiveFrom} onChange={(e) => setLocation({ ...location, effectiveFrom: e.target.value })} {...dateField} />
                    <TextField label="Change reason" value={location.changeReason} onChange={(e) => setLocation({ ...location, changeReason: e.target.value })} fullWidth />
                  </FieldGrid>
                  <Box sx={{ mt: 2 }}>
                    {data?.location && !data.location.acknowledged_at && (
                      <Button onClick={() => acknowledge.mutate({
                        staffLocationId: data.location.id,
                        clauseText: data.defaultClause,
                        method: 'on_screen',
                      })}>Record the location declaration</Button>
                    )}
                    {data?.location?.acknowledged_at && <Alert severity="success">Current location is acknowledged.</Alert>}
                  </Box>
                </Section>
              </Stack>
            )}

            {tab === 3 && (
              <Section title="Education, previous job, and documents" hint="Optional. Payroll does not wait on these.">
                <ExtraRows userId={userId} documents={data?.documents || []} educationRows={data?.education || []} jobRows={data?.previousEmployment || []} emergency={form} set={set} onSavePerson={() => saveProfile.mutate(saveBody)} />
              </Section>
            )}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}

function WorkPattern({ userId, role, country, form, pattern, leaveTemplateId, holidayCalendarId, templates, calendars }) {
  const [workingDays, setWorkingDays] = useState('');
  const [offs, setOffs] = useState([]);
  const [hours, setHours] = useState('');
  const [leaveId, setLeaveId] = useState('');
  const [calendarId, setCalendarId] = useState('');
  useEffect(() => {
    setWorkingDays(pattern?.working_days_per_week ?? '');
    setOffs((pattern?.weekly_off_days || []).map(Number));
    setHours(pattern?.hours_per_day ?? '');
    const match = (row) => row.role === role && String(row.country || '').toLowerCase() === String(country || '').toLowerCase();
    setLeaveId(leaveTemplateId || templates.find(match)?.id || '');
    setCalendarId(holidayCalendarId || calendars.find(match)?.id || '');
  }, [pattern, leaveTemplateId, holidayCalendarId, templates, calendars, role, country]);
  return (
    <Section
      title="Week, holidays, and leave"
      hint="Days off are paid. Choose the leave list and holiday list from Leave and holidays."
      action={<Button variant="contained" onClick={() => staffLeaveAPI.savePerson(userId, {
        dateOfJoining: form.dateOfJoining,
        exitDate: form.exitDate || null,
        workingDaysPerWeek: Number(workingDays),
        weeklyOffDays: offs,
        hoursPerDay: Number(hours),
        leaveTemplateId: leaveId,
        holidayCalendarId: calendarId,
      }).then(() => toast.success('Work pattern saved')).catch((e) => toast.error(e.response?.data?.message || 'Request failed'))}>Save work pattern</Button>}
    >
      <FieldGrid>
        <TextField label="Working days each week" type="number" value={workingDays} onChange={(e) => setWorkingDays(e.target.value)} fullWidth />
        <TextField label="Hours each day" type="number" value={hours} onChange={(e) => setHours(e.target.value)} fullWidth />
        <TextField select label="Leave list" value={leaveId} onChange={(e) => setLeaveId(e.target.value)} fullWidth>
          {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
        <TextField select label="Holiday list" value={calendarId} onChange={(e) => setCalendarId(e.target.value)} fullWidth>
          {calendars.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
        </TextField>
      </FieldGrid>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>Days off each week</Typography>
      <ToggleButtonGroup value={offs} onChange={(_, value) => setOffs(value)} size="small">
        {DAYS.map((day) => <ToggleButton key={day.value} value={day.value}>{day.label}</ToggleButton>)}
      </ToggleButtonGroup>
    </Section>
  );
}

const EXCLUDED = new Set(['House rent allowance', 'Conveyance allowance']);

function lineRupees(line, totalNumber) {
  if (line.mode === 'rupees') return Number(line.amount || 0);
  if (!Number.isFinite(totalNumber)) return Number(line.amount || 0);
  return Math.floor((totalNumber * Number(line.percentage || 0)) / 100 + 0.5);
}

function fillRest(rows, total) {
  const index = rows.findIndex((line) => line.takesRest);
  const totalNumber = Number(total);
  const priced = rows.map((line, i) => {
    if (i === index) return line;
    const amount = lineRupees(line, totalNumber);
    const percentage = line.mode === 'rupees' && Number.isFinite(totalNumber) && totalNumber > 0
      ? String(Math.round((amount / totalNumber) * 10000) / 100)
      : line.percentage;
    return { ...line, amount: Number.isFinite(amount) ? String(amount) : '', percentage };
  });
  if (index < 0 || !Number.isFinite(totalNumber)) return priced;
  const usedAmt = priced.reduce((n, line, i) => (i === index ? n : n + Number(line.amount || 0)), 0);
  const rest = Math.round((totalNumber - usedAmt) * 100) / 100;
  return priced.map((line, i) => (i === index ? {
    ...line,
    amount: String(rest),
    percentage: totalNumber > 0 ? String(Math.round((rest / totalNumber) * 10000) / 100) : '',
  } : line));
}

function fromTemplate(templateLines, total) {
  const totalNumber = Number(total);
  const rows = templateLines.map((line) => {
    const takesRest = Boolean(line.takes_rest);
    const rupees = line.fixed_amount != null && line.fixed_amount !== '';
    const pct = Number(line.percentage);
    return {
      lineName: line.line_name || line.lineName,
      mode: rupees ? 'rupees' : 'percent',
      percentage: takesRest || rupees ? '' : String(line.percentage),
      amount: rupees
        ? String(line.fixed_amount)
        : (Number.isFinite(totalNumber) && !takesRest ? String(Math.floor((totalNumber * pct) / 100 + 0.5)) : ''),
      takesRest,
    };
  });
  if (!rows.some((line) => line.takesRest)) {
    const balancer = rows.find((line) => line.lineName === 'Special allowance');
    if (balancer) balancer.takesRest = true;
  }
  return fillRest(rows, total);
}

function suggestedPay(payeeType, total) {
  if (payeeType === 'contractor') {
    return fillRest([{ lineName: 'Professional fee', mode: 'percent', percentage: '100', amount: '', takesRest: true }], total);
  }
  return fillRest([
    { lineName: 'Basic pay', mode: 'percent', percentage: '50', amount: '', takesRest: false },
    { lineName: 'Special allowance', mode: 'percent', percentage: '', amount: '', takesRest: true },
  ], total);
}

function SalaryLines({ payeeType, templates, onSave }) {
  const [period, setPeriod] = useState('monthly');
  const [total, setTotal] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [lines, setLines] = useState(() => suggestedPay(payeeType, ''));
  const chosen = templates.find((t) => t.id === templateId);
  const shown = fillRest(lines, total);
  const sum = shown.reduce((n, line) => n + Number(line.amount || 0), 0);
  const excluded = shown.filter((line) => EXCLUDED.has(line.lineName)).reduce((n, line) => n + Number(line.amount || 0), 0);
  const totalNumber = Number(total);
  const restShort = shown.some((line) => line.takesRest && Number(line.amount) < 0);
  const changeTotal = (value) => {
    setTotal(value);
    const totalNumberNext = Number(value);
    if (!Number.isFinite(totalNumberNext)) return;
    setLines(fillRest(lines.map((line) => (line.takesRest || line.mode === 'rupees' ? line : {
      ...line,
      amount: String(Math.floor((totalNumberNext * Number(line.percentage || 0)) / 100 + 0.5)),
    })), value));
  };
  const editPercent = (index, percentage) => {
    const next = lines.map((line, i) => (i === index ? {
      ...line,
      mode: 'percent',
      percentage,
      amount: Number.isFinite(totalNumber) ? String(Math.floor((totalNumber * Number(percentage || 0)) / 100 + 0.5)) : line.amount,
    } : line));
    setLines(next);
  };
  return (
    <Section
      title="Agreed pay"
      hint="Type the total. A part can be a percent or a fixed amount. Special allowance fills whatever is left."
      action={<Button variant="contained" disabled={!Number.isFinite(totalNumber) || restShort || Math.abs(sum - totalNumber) > 0.001} onClick={() => onSave({
        templateId: chosen?.id || null,
        period,
        totalAmount: totalNumber,
        effectiveFrom,
        lines: shown.filter((line) => line.lineName).map((line) => ({
          lineName: line.lineName,
          percentage: Number(line.percentage || 0),
          amount: Number(line.amount),
        })),
      })}>Save pay</Button>}
    >
      <FieldGrid>
        <TextField select label="How often" value={period} onChange={(e) => setPeriod(e.target.value)} fullWidth>
          <MenuItem value="monthly">Monthly</MenuItem>
          <MenuItem value="yearly">Yearly</MenuItem>
        </TextField>
        <TextField label="Total pay" type="number" value={total} onChange={(e) => changeTotal(e.target.value)} fullWidth />
        <TextField label="Starting from" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} {...dateField} />
        <TextField select label="Salary template" value={templateId} onChange={(e) => {
          const id = e.target.value;
          setTemplateId(id);
          const template = templates.find((t) => t.id === id);
          if (template) setLines(fromTemplate(template.lines, total));
        }} fullWidth>
          <MenuItem value="">Suggested split</MenuItem>
          {templates.filter((t) => !payeeType || t.payee_type === payeeType).map((t) => (
            <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
          ))}
        </TextField>
      </FieldGrid>
      <Button size="small" sx={{ mt: 2 }} onClick={() => setLines(suggestedPay(payeeType, total))}>Use suggested split</Button>
      <Stack spacing={1} sx={{ mt: 1 }}>
        {shown.map((line, index) => (
          <Box key={`${line.lineName}-${index}`} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '2fr 100px 1fr auto' }, gap: 1, alignItems: 'center' }}>
            <TextField size="small" label="Part of pay" value={line.lineName} onChange={(e) => {
              const next = [...lines];
              next[index] = { ...lines[index], lineName: e.target.value };
              setLines(next);
            }} />
            {line.takesRest ? (
              <TextField size="small" label="Whatever is left" value={line.amount} disabled />
            ) : line.mode === 'rupees' ? (
              <TextField size="small" label="Fixed rupees" type="number" value={lines[index]?.amount || ''} onChange={(e) => {
                const next = lines.map((row, i) => (i === index ? { ...row, mode: 'rupees', amount: e.target.value } : row));
                setLines(next);
              }} />
            ) : (
              <TextField size="small" label="Percent" type="number" value={line.percentage} onChange={(e) => editPercent(index, e.target.value)} />
            )}
            {!line.takesRest && (
              <Button size="small" onClick={() => {
                const next = lines.map((row, i) => (i === index ? { ...row, mode: row.mode === 'rupees' ? 'percent' : 'rupees' } : row));
                setLines(next);
              }}>{line.mode === 'rupees' ? 'Use percent' : 'Use a fixed amount'}</Button>
            )}
            {line.mode === 'percent' && !line.takesRest && <TextField size="small" label="Rupees" value={line.amount} disabled />}
            <Button size="small" onClick={() => setLines(lines.filter((_, i) => i !== index))} disabled={lines.length === 1}>Remove</Button>
          </Box>
        ))}
      </Stack>
      {restShort && <Alert severity="warning" sx={{ mt: 2 }}>The other parts are already more than the total. Lower one of them.</Alert>}
      {Number.isFinite(totalNumber) && totalNumber > 0 && excluded > totalNumber / 2 && (
        <Alert severity="info" sx={{ mt: 2 }}>House rent and conveyance are more than half. The extra is counted as wages. The amounts stay as shown.</Alert>
      )}
    </Section>
  );
}

function IncentiveAndStatute({ userId, categories, statutes }) {
  const [incentive, setIncentive] = useState({ categoryId: '', startDate: '', endDate: '', amount: '' });
  const [statute, setStatute] = useState({ statuteCode: 'pf', status: 'confirmed', reason: '', effectiveFrom: '' });
  const saveIncentive = () => payrollAPI.saveIncentive(userId, {
    ...incentive,
    amount: Number(incentive.amount),
  }).then(() => toast.success('Incentive saved')).catch((e) => toast.error(e.response?.data?.message || 'Request failed'));
  const saveStatute = () => payrollAPI.setApplicability(userId, statute)
    .then(() => toast.success('Applicability saved'))
    .catch((e) => toast.error(e.response?.data?.message || 'Request failed'));
  const typed = categories.filter((c) => !['demo', 'trial', 'yr_retainer'].includes(c.code));
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Section title="Extra monthly amount" hint="Demo, Trial, and 1YrRetainer are counted on Payroll. Use this for any other amount." action={<Button variant="contained" onClick={saveIncentive} disabled={!incentive.categoryId}>Save incentive</Button>}>
        <Stack spacing={2}>
          <TextField select label="Category" value={incentive.categoryId} onChange={(e) => setIncentive({ ...incentive, categoryId: e.target.value })} fullWidth>
            {typed.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField label="Start" value={incentive.startDate} onChange={(e) => setIncentive({ ...incentive, startDate: e.target.value })} {...dateField} />
          <TextField label="End" value={incentive.endDate} onChange={(e) => setIncentive({ ...incentive, endDate: e.target.value })} {...dateField} />
          <TextField label="Amount each month" type="number" value={incentive.amount} onChange={(e) => setIncentive({ ...incentive, amount: e.target.value })} fullWidth />
        </Stack>
      </Section>
      <Section title="What applies to this person" hint="Turn a deduction or contribution on. Payroll uses it only after this is saved." action={<Button variant="contained" onClick={saveStatute}>Save</Button>}>
        <Stack spacing={2}>
          <TextField select label="Which one" value={statute.statuteCode} onChange={(e) => setStatute({ ...statute, statuteCode: e.target.value })} fullWidth>
            {statutes.map((s) => <MenuItem key={s.code} value={s.code}>{statuteLabel(s.code)}</MenuItem>)}
          </TextField>
          <TextField select label="Does it apply" value={statute.status} onChange={(e) => setStatute({ ...statute, status: e.target.value })} fullWidth>
            <MenuItem value="confirmed">Yes</MenuItem>
            <MenuItem value="override_on">Yes, even if the usual rule says no</MenuItem>
            <MenuItem value="override_off">No, even if the usual rule says yes</MenuItem>
          </TextField>
          <TextField label="Reason" value={statute.reason} onChange={(e) => setStatute({ ...statute, reason: e.target.value })} helperText="Required for an override" fullWidth />
          <TextField label="Effective from" value={statute.effectiveFrom} onChange={(e) => setStatute({ ...statute, effectiveFrom: e.target.value })} {...dateField} />
        </Stack>
      </Section>
    </Box>
  );
}

function ExtraRows({ userId, documents, educationRows, jobRows, emergency, set, onSavePerson }) {
  const [education, setEducation] = useState({ qualification: '', collegeName: '', collegeCity: '', universityName: '', degreeOrCourse: '', subject: '' });
  const [job, setJob] = useState({ companyName: '', companyAddress: '', designation: '', salary: '', reportingManagerName: '' });
  const [doc, setDoc] = useState({ documentType: '', storageKey: '' });
  const save = (fn, body) => fn(userId, body).then(() => toast.success('Saved')).catch((e) => toast.error(e.response?.data?.message || 'Request failed'));
  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>Education</Typography>
        <FieldGrid>
          <TextField label="Qualification" value={education.qualification} onChange={(e) => setEducation({ ...education, qualification: e.target.value })} fullWidth />
          <TextField label="College" value={education.collegeName} onChange={(e) => setEducation({ ...education, collegeName: e.target.value })} fullWidth />
          <TextField label="Degree or course" value={education.degreeOrCourse} onChange={(e) => setEducation({ ...education, degreeOrCourse: e.target.value })} fullWidth />
        </FieldGrid>
        <Button sx={{ mt: 1 }} onClick={() => save(payrollAPI.addEducation, education)}>Add education</Button>
        {(educationRows || []).map((row) => (
          <Typography key={row.id} variant="body2">{row.qualification || row.degree_or_course} · {row.college_name}</Typography>
        ))}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>Previous job</Typography>
        <FieldGrid>
          <TextField label="Company" value={job.companyName} onChange={(e) => setJob({ ...job, companyName: e.target.value })} fullWidth />
          <TextField label="Designation" value={job.designation} onChange={(e) => setJob({ ...job, designation: e.target.value })} fullWidth />
          <TextField label="Salary" value={job.salary} onChange={(e) => setJob({ ...job, salary: e.target.value })} fullWidth />
        </FieldGrid>
        <Button sx={{ mt: 1 }} onClick={() => save(payrollAPI.addPreviousEmployment, { ...job, salary: job.salary === '' ? null : Number(job.salary) })}>Add previous job</Button>
        {(jobRows || []).map((row) => (
          <Typography key={row.id} variant="body2">{row.company_name} · {row.designation}</Typography>
        ))}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>Document reference</Typography>
        <FieldGrid>
          <TextField label="Document type" value={doc.documentType} onChange={(e) => setDoc({ ...doc, documentType: e.target.value })} fullWidth />
          <TextField label="Where the file is kept" value={doc.storageKey} onChange={(e) => setDoc({ ...doc, storageKey: e.target.value })} fullWidth />
        </FieldGrid>
        <Button sx={{ mt: 1 }} onClick={() => save(payrollAPI.addDocument, doc)}>Add document</Button>
        {documents.map((row) => (
          <Typography key={row.id} variant="body2">{row.document_type}: {row.file_name || row.storage_key}</Typography>
        ))}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ mb: 1 }}>Emergency contact</Typography>
        <FieldGrid>
          <TextField label="Name" value={emergency.emergencyName} onChange={set('emergencyName')} fullWidth />
          <TextField label="Phone" value={emergency.emergencyPhone} onChange={set('emergencyPhone')} fullWidth />
          <TextField label="Relation" value={emergency.emergencyRelation} onChange={set('emergencyRelation')} fullWidth />
          <TextField label="Aadhaar" value={emergency.aadhaarNumber} onChange={set('aadhaarNumber')} fullWidth />
          <TextField label="Nationality" value={emergency.nationality} onChange={set('nationality')} fullWidth />
          <TextField label="Marital status" value={emergency.maritalStatus} onChange={set('maritalStatus')} fullWidth />
          <TextField label="Total experience (years)" value={emergency.totalExperience} onChange={set('totalExperience')} fullWidth />
          <TextField label="Relevant experience (years)" value={emergency.relevantExperience} onChange={set('relevantExperience')} fullWidth />
          <TextField label="Google Pay" value={emergency.googlePay} onChange={set('googlePay')} fullWidth />
          <TextField label="Permanent address" value={emergency.permanentAddress} onChange={set('permanentAddress')} fullWidth />
          <TextField label="Previous PF or ESI note" value={emergency.previousPfEsiNote} onChange={set('previousPfEsiNote')} fullWidth />
        </FieldGrid>
        <Button sx={{ mt: 1 }} variant="contained" onClick={onSavePerson}>Save these details</Button>
      </Box>
    </Stack>
  );
}
